import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import useUserStore from '@/state/userState';
import useOrganizationStore from '@/state/orgState';
import { fetchUserAPIKey, generateAPIKey, deleteApiKey } from '@/apis/apiKeys';
import posthog from '@/lib/posthog';
import { toast } from '@/utils/toastManager';

export const useApiKeys = () => {
  const { data: session } = useSession();
  const { user } = useUserStore();
  const { organization, setApiKey } = useOrganizationStore();
  const queryClient = useQueryClient();

  // Query to fetch API key
  const apiKeyQuery = useQuery({
    queryKey: ['apiKey', user?.UserUUID],
    queryFn: async () => {
      if (!user?.UserUUID) {
        throw new Error('User UUID is required');
      }

      const response = await fetchUserAPIKey({
        UserUUID: user.UserUUID,
      });

      // Check if there's an error in the response
      if (response.Error && response.Error !== null) {
        throw new Error(response.Error);
      }

      // Check if the API key exists in the response and is not empty
      const apiKey =
        response.ApiKey &&
        typeof response.ApiKey === 'string' &&
        response.ApiKey.trim() !== ''
          ? response.ApiKey
          : null;

      // Update Zustand store when query succeeds
      if (apiKey) {
        setApiKey(apiKey);
      } else {
        setApiKey('');
      }

      // Return both key and creation date
      return {
        apiKey,
        createdAt: response.CreatedAt || null,
      };
      return {
        apiKey,
        createdAt: response.CreatedAt || null,
      };
    },
    enabled: !!user?.UserUUID && !!session?.user?.accessToken,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes when unused
    retry: 1,
  });

  // Mutation to generate/regenerate API key
  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!user?.UserUUID) {
        throw new Error('User UUID is required');
      }

      posthog.capture('api_key_generation_started', {
        github_username: user?.GithubUsername,
      });

      const response = await generateAPIKey({
        UserUUID: user.UserUUID,
      });

      if (response.Error && response.Error !== null) {
        throw new Error(response.Error);
      }

      if (!response.ApiKey) {
        throw new Error('Failed to generate API key');
      }

      return {
        apiKey: response.ApiKey,
        createdAt: response.CreatedAt || null,
      };
    },
    onSuccess: (data) => {
      if (!user?.UserUUID) {
        console.error('User UUID is missing in onSuccess');
        return;
      }

      const apiKey = data.apiKey;
      const hadExistingKey =
        !!apiKeyQuery.data?.apiKey && apiKeyQuery.data.apiKey.trim() !== '';

      // Use the key directly from the mutation response - it's the source of truth
      // Update Zustand store immediately
      setApiKey(apiKey);

      // Update React Query cache immediately with the key and creation date from mutation response
      // Don't invalidate - trust the mutation response, it's the latest data
      const queryKey = ['apiKey', user.UserUUID] as const;
      queryClient.setQueryData(queryKey, data);

      posthog.capture('api_key_generation_completed', {
        github_username: user?.GithubUsername,
        is_regeneration: hadExistingKey,
      });

      toast.show({
        title: hadExistingKey ? 'API key regenerated' : 'API key generated',
        description: hadExistingKey
          ? 'Your old API key has been invalidated'
          : undefined,
        status: 'success',
        isClosable: true,
      });
    },
    onError: (error) => {
      posthog.capture('api_key_generation_error', {
        error_message: error.message,
        github_username: user?.GithubUsername,
      });

      toast.show({
        title: 'Failed to generate API key',
        description: error.message,
        status: 'error',
        isClosable: true,
      });
    },
  });

  // Mutation to delete API key
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!user?.UserUUID) {
        throw new Error('User UUID is required');
      }

      posthog.capture('api_key_deletion_started', {
        github_username: user?.GithubUsername,
      });

      const response = await deleteApiKey({
        UserUUID: user.UserUUID,
      });

      if (!response.Success) {
        throw new Error(response.Error || 'Failed to delete API key');
      }

      return response;
    },
    onSuccess: () => {
      if (!user?.UserUUID) {
        console.error('User UUID is missing in delete onSuccess');
        return;
      }

      // Update Zustand store immediately
      setApiKey('');

      // Update React Query cache immediately - trust the mutation response
      // Don't invalidate - the mutation confirmed deletion, so set to null
      const queryKey = ['apiKey', user.UserUUID] as const;
      queryClient.setQueryData(queryKey, null);

      posthog.capture('api_key_deleted', {
        github_username: user?.GithubUsername,
      });

      toast.show({
        title: 'API key deleted successfully',
        status: 'success',
        isClosable: true,
      });
    },
    onError: (error) => {
      posthog.capture('api_key_deletion_error', {
        error_message: error.message,
        github_username: user?.GithubUsername,
      });

      toast.show({
        title: 'Failed to delete API key',
        description: error.message,
        status: 'error',
        isClosable: true,
      });
    },
  });

  // Derive apiKeys array from the query data
  const apiKeyData = apiKeyQuery.data;
  const apiKey =
    apiKeyData?.apiKey || (typeof apiKeyData === 'string' ? apiKeyData : null);
  const createdAt = apiKeyData?.createdAt || null;

  // Memoize apiKeys array to prevent unnecessary recalculations
  const apiKeys = React.useMemo(() => {
    if (apiKey && typeof apiKey === 'string' && apiKey.trim() !== '') {
      return [
        {
          OrgName: organization?.OrgName || 'Default',
          apiKey: apiKey,
          createdAt: createdAt,
        },
      ];
    }
    return [];
  }, [apiKey, createdAt, organization?.OrgName]);

  return {
    apiKey,
    apiKeys,
    isLoading: apiKeyQuery.isPending,
    isFetching: apiKeyQuery.isFetching,
    isGenerating: generateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    error: apiKeyQuery.error || generateMutation.error || deleteMutation.error,
    generateApiKey: () => generateMutation.mutate(),
    deleteApiKey: () => deleteMutation.mutate(),
    refetch: () => apiKeyQuery.refetch(),
  };
};
